import { gsap } from "../../animations/gsapSetup.ts";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useIsMobile } from "../../hooks/useMediaQuery.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./ErrorClassification.module.css";
import { COLOR_OK, GAUGE_CIRCUM, type GaugeSegments, TriColorGauge } from "./TriColorGauge.tsx";

/* ── Gauge Data ── */

interface ErrorGauge {
  id: string;
  segments: GaugeSegments;
  leftLabel: string;
  rightLabel: string;
  trend: string;
  trendType: "positive" | "negative";
}

const ERROR_GAUGES: Omit<ErrorGauge, "leftLabel" | "rightLabel">[] = [
  {
    id: "arc",
    segments: { ext1: 3, warn1: 12, ok: 68, warn2: 12, ext2: 5 },
    trend: "+5%",
    trendType: "positive",
  },
  {
    id: "depth",
    segments: { ext1: 7, warn1: 18, ok: 52, warn2: 15, ext2: 8 },
    trend: "-3%",
    trendType: "negative",
  },
  {
    id: "direction",
    segments: { ext1: 4, warn1: 14, ok: 62, warn2: 12, ext2: 8 },
    trend: "+2%",
    trendType: "positive",
  },
];

export function ErrorClassification() {
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const { t } = useI18n();

  const gl = t.ai.gaugeLabels;
  const columns = [
    { ...t.ai.errorArc, gauge: ERROR_GAUGES[0], left: gl.arc.left, right: gl.arc.right },
    { ...t.ai.errorDepth, gauge: ERROR_GAUGES[1], left: gl.depth.left, right: gl.depth.right },
    {
      ...t.ai.errorDirection,
      gauge: ERROR_GAUGES[2],
      left: gl.direction.left,
      right: gl.direction.right,
    },
  ];

  const ref = useGsapScrollTrigger(
    (el) => {
      if (reducedMotion) return;

      const cards = el.querySelectorAll(`.${styles.card}`);

      if (isMobile) {
        gsap.set(cards, { y: 40, opacity: 0 });
        gsap.to(cards, {
          y: 0,
          opacity: 1,
          duration: 0.7,
          stagger: 0.15,
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });
      } else {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: el,
            pin: true,
            scrub: 1,
            start: "top top",
            end: "+=200%",
          },
        });

        /* Header */
        const header = el.querySelector(`.${styles.title}`);
        if (header) {
          gsap.set(header, { y: 30, opacity: 0 });
          tl.to(header, { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }, 0);
        }

        /* Cards stagger in */
        let idx = 0;
        for (const card of cards) {
          gsap.set(card, { y: 50, opacity: 0 });
          tl.to(card, { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }, 0.4 + idx * 0.4);

          /* Gauge 5-segment fill animation */
          const gaugeSegs = card.querySelectorAll("[data-gauge-seg]");
          let segIdx = 0;
          for (const seg of gaugeSegs) {
            const segEl = seg as SVGCircleElement;
            const targetLen = Number(segEl.dataset.gaugeSeg) || 0;
            const offset = Number(segEl.dataset.gaugeOffset) || 0;
            gsap.set(segEl, {
              strokeDasharray: `0 ${GAUGE_CIRCUM}`,
              strokeDashoffset: -offset,
            });
            tl.to(
              segEl,
              {
                strokeDasharray: `${targetLen} ${GAUGE_CIRCUM - targetLen}`,
                duration: 0.8,
                ease: "power2.out",
              },
              0.8 + idx * 0.4 + segIdx * 0.1,
            );
            segIdx++;
          }

          /* OK percentage counter */
          const okText = card.querySelector("[data-gauge-ok]") as SVGTextElement | null;
          if (okText) {
            const target = Number(okText.dataset.gaugeOk) || 0;
            const obj = { val: 0 };
            tl.to(
              obj,
              {
                val: target,
                duration: 1.2,
                ease: "power2.out",
                onUpdate: () => {
                  okText.textContent = `${Math.round(obj.val)}%`;
                },
              },
              0.8 + idx * 0.4,
            );
          }

          /* Trend badge */
          const badge = card.querySelector(`.${styles.trendBadge}`);
          if (badge) {
            gsap.set(badge, { scale: 0, opacity: 0 });
            tl.to(
              badge,
              { scale: 1, opacity: 1, duration: 0.3, ease: "back.out(2)" },
              1.6 + idx * 0.4,
            );
          }

          idx++;
        }
      }
    },
    [isMobile],
  );

  return (
    <section ref={ref} className={styles.section}>
      <div className={styles.inner}>
        <h2 className={styles.title}>{t.ai.errorTitle}</h2>
        <div className={styles.cards}>
          {columns.map((col) => (
            <div key={col.gauge.id} className={styles.card}>
              {/* Tri-Color Gauge */}
              <div className={styles.gaugeWrap}>
                <TriColorGauge
                  segments={col.gauge.segments}
                  label={col.title}
                  leftLabel={col.left}
                  rightLabel={col.right}
                />
              </div>

              {/* Health-style card body */}
              <div className={styles.cardBody}>
                <div className={styles.cardHeader}>
                  <span className={styles.cardDot} style={{ background: COLOR_OK }} />
                  <span className={styles.cardTitle}>{col.title}</span>
                  <span
                    className={`${styles.trendBadge} ${
                      col.gauge.trendType === "positive"
                        ? styles.trendPositive
                        : styles.trendNegative
                    }`}
                  >
                    {col.gauge.trend}
                  </span>
                </div>
                <p className={styles.cardDesc}>{col.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
